import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { LoginForm, ProviderChooserForm } from '../features';
import {
  getIdpRedirectUrl,
  getLoginPageInfo,
  type IdpUrlProps,
} from '../features/login-form/data-sources';
import { LoadingState, WindowContext } from '../shared/components';
import {
  getAuthenticationStatus,
  getIdentityProviders,
} from '../shared/data-sources';
import { type IdentityProvider } from '../shared/types';
import { urls } from '../shared/utils';

/**
 * Login Page
 * @returns JSX.Element
 */
function LoginPage(): JSX.Element | null {
  const { locationAssign } = useContext(WindowContext);
  const history = useHistory();

  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [hasProviderError, setHasProviderError] = useState(false);
  const [providers, setProviders] = useState<IdentityProvider[] | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [version, setVersion] = useState('');

  const [isLoadingRedirect, setIsLoadingRedirect] = useState(false);
  const [hasRedirectError, setHasRedirectError] = useState(false);
  const [redirectErrorMessage, setRedirectErrorMessage] = useState('');

  const isValid = !isLoadingProviders && !hasProviderError;
  const needsLogin = isValid && providers?.length === 0;
  const hasOneIdp = isValid && providers?.length === 1;
  const year = new Date().getFullYear();
  const versionMessage = `Saviynt v${version}, © Copyright ${year}, Saviynt All Rights Reserved. Use is subject to license terms.`;

  const navigateToIdp = (props: IdpUrlProps) => {
    setIsLoadingRedirect(true);
    setHasRedirectError(false);
    setRedirectErrorMessage('');

    getIdpRedirectUrl(props)
      .then((redirectUrl) => {
        locationAssign(redirectUrl);
      })
      .catch((error: Error) => {
        setHasRedirectError(true);
        setRedirectErrorMessage(error.message || '');
      })
      .finally(() => {
        setIsLoadingRedirect(false);
      });
  };

  useEffect(() => {
    // These 2 API calls should be sone in series to avoid having 2 navigations
    //  if there is only 1 IDP and the user is already logged in.
    (async () => {
      const { isAuthenticated } = await getAuthenticationStatus();

      if (isAuthenticated) {
        // Navigate away if we're already auth'd
        history.push('/ECMv6/request/requestHome');

        return;
      }

      const result = await getIdentityProviders();
      // Test mode providers should not appear on the login page (UI-1335)
      const enabledProviders = result.filter(
        (p) => p.isEnabled && !p.isTestMode
      );

      setProviders(enabledProviders);

      const info = await getLoginPageInfo();

      setWelcomeMessage(info.welcomeMessage);
      setVersion(info.version);

      if (enabledProviders.length === 1) {
        navigateToIdp({ alias: enabledProviders[0].alias });
      }
    })()
      .catch(() => {
        setHasProviderError(true);
      })
      .finally(() => {
        setIsLoadingProviders(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only run on mount

  if (isLoadingProviders || (hasOneIdp && isLoadingRedirect)) {
    return <LoadingState />;
  }

  if (hasOneIdp) {
    // No need to render anything when navigating away
    return null;
  }

  if (needsLogin) {
    return (
      <LoginForm
        welcomeMessage={welcomeMessage}
        versionMessage={versionMessage}
      />
    );
  }

  return (
    <ProviderChooserForm
      welcomeMessage={welcomeMessage}
      versionMessage={versionMessage}
      providers={providers ?? []}
      onChoose={(props) => {
        navigateToIdp(props);
      }}
      isError={hasProviderError || hasRedirectError}
      isLoading={isLoadingRedirect}
      errorMessage={redirectErrorMessage}
    />
  );
}

export const Component = LoginPage;
export const title = 'Login';
export const path = urls[title];
